import type { OverridedMixpanel } from 'mixpanel-browser';
import { z } from 'zod';
import { getOrCreateAnonId, readStoredAttributionState } from '@/lib/attribution';
import { getTraceId, logInfo, logWarn } from '@/lib/logger';

const COOKIE_CONSENT_STORAGE_KEY = 'dwellbot_cookie_consent';

const emptyStringAsUndefined = (value: unknown): unknown =>
  typeof value === 'string' && value.trim().length === 0 ? undefined : value;

const booleanFromEnv = z.preprocess(
  emptyStringAsUndefined,
  z
    .string()
    .optional()
    .transform((value) => value === 'true')
    .default('false')
);

const MixpanelEnvSchema = z.object({
  browserEnabled: booleanFromEnv,
  projectToken: z.preprocess(emptyStringAsUndefined, z.string().trim().min(1).optional()),
  apiHost: z.preprocess(
    emptyStringAsUndefined,
    z.string().trim().url().default('https://api.mixpanel.com')
  ),
  debug: booleanFromEnv,
  replayEnabled: booleanFromEnv,
  requiresCookieConsent: booleanFromEnv,
});

const CookieConsentStateSchema = z.object({
  version: z.string().min(1),
  requiresCookieConsent: z.boolean(),
  analytics: z.enum(['granted', 'denied']),
  updatedAtIso: z.string().min(1),
});

type MixpanelSdk = OverridedMixpanel;
type MixpanelReplaySurface = 'auth' | 'challenge' | 'share';
type CookieConsentState = z.infer<typeof CookieConsentStateSchema>;
type MixpanelBrowserConfig = z.infer<typeof MixpanelEnvSchema>;

let currentConfig: MixpanelBrowserConfig | null = null;
let mixpanelLoadPromise: Promise<MixpanelSdk | null> | null = null;
let loadedMixpanelSdk: MixpanelSdk | null = null;
let identifiedUserId: string | null = null;
const activeReplaySurfaces = new Set<MixpanelReplaySurface>();

const readStoredJson = (storageKey: string): string | null => {
  try {
    const storedValue = window.localStorage.getItem(storageKey);
    return storedValue;
  } catch {
    return null;
  }
};

const readCookieConsentState = (): CookieConsentState | null => {
  const storedValue = readStoredJson(COOKIE_CONSENT_STORAGE_KEY);

  if (storedValue === null) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(storedValue) as unknown;
    const validatedValue = CookieConsentStateSchema.parse(parsedValue);
    return validatedValue;
  } catch {
    return null;
  }
};

const currentMixpanelConfig = (): MixpanelBrowserConfig => {
  if (currentConfig !== null) {
    return currentConfig;
  }

  currentConfig = MixpanelEnvSchema.parse({
    browserEnabled: import.meta.env.VITE_MIXPANEL_BROWSER_ENABLED,
    projectToken: import.meta.env.VITE_MIXPANEL_PROJECT_TOKEN,
    apiHost: import.meta.env.VITE_MIXPANEL_API_HOST,
    debug: import.meta.env.VITE_MIXPANEL_DEBUG,
    replayEnabled: import.meta.env.VITE_MIXPANEL_REPLAY_ENABLED,
    requiresCookieConsent: import.meta.env.VITE_MIXPANEL_REQUIRES_COOKIE_CONSENT,
  });

  return currentConfig;
};

const isCookieConsentGranted = (config: MixpanelBrowserConfig): boolean => {
  if (!config.requiresCookieConsent) {
    return true;
  }

  const cookieConsentState = readCookieConsentState();

  if (cookieConsentState === null) {
    return false;
  }

  if (!cookieConsentState.requiresCookieConsent) {
    return true;
  }

  return cookieConsentState.analytics === 'granted';
};

const isBrowserTrackingAllowed = (config: MixpanelBrowserConfig): boolean => {
  if (!config.browserEnabled) {
    return false;
  }

  if (!config.projectToken) {
    return false;
  }

  return isCookieConsentGranted(config);
};

const buildBaseEventProperties = (): Record<string, unknown> => {
  const anonId = getOrCreateAnonId();
  const storedAttributionState = readStoredAttributionState();
  const baseEventProperties = {
    trace_id: getTraceId(),
    anon_id: anonId,
    landing_touch_id: storedAttributionState?.landingTouchId ?? null,
    landing_surface_type: storedAttributionState?.landingSurfaceType ?? null,
    landing_surface_key: storedAttributionState?.landingSurfaceKey ?? null,
    challenge_share_id: storedAttributionState?.challengeShareId ?? null,
    challenge_public_id: storedAttributionState?.challengePublicId ?? null,
  };

  return baseEventProperties;
};

const applyAnonIdentity = (mixpanelSdk: MixpanelSdk): void => {
  const anonId = getOrCreateAnonId();

  mixpanelSdk.register({
    anon_id: anonId,
  });
};

const loadMixpanelSdk = async (): Promise<MixpanelSdk | null> => {
  const config = currentMixpanelConfig();

  if (!isBrowserTrackingAllowed(config)) {
    return null;
  }

  if (loadedMixpanelSdk !== null) {
    return loadedMixpanelSdk;
  }

  if (mixpanelLoadPromise !== null) {
    return mixpanelLoadPromise;
  }

  mixpanelLoadPromise = (async () => {
    try {
      const mixpanelModule = await import('mixpanel-browser');
      const mixpanelSdk = mixpanelModule.default;

      mixpanelSdk.init(config.projectToken!, {
        api_host: config.apiHost,
        autocapture: false,
        debug: config.debug,
        persistence: 'localStorage',
        record_sessions_percent: 0,
        track_pageview: false,
      });

      applyAnonIdentity(mixpanelSdk);
      loadedMixpanelSdk = mixpanelSdk;

      return mixpanelSdk;
    } catch (error) {
      mixpanelLoadPromise = null;
      throw error;
    }
  })();

  return mixpanelLoadPromise;
};

export const initMixpanelAnalytics = (): MixpanelBrowserConfig => {
  const config = currentMixpanelConfig();
  const trackingAllowed = isBrowserTrackingAllowed(config);

  logInfo('analytics.mixpanel.config', 'Mixpanel browser config evaluated', {
    trace_id: getTraceId(),
    browser_enabled: config.browserEnabled,
    replay_enabled: config.replayEnabled,
    requires_cookie_consent: config.requiresCookieConsent,
    tracking_allowed: trackingAllowed,
  });

  return config;
};

export const identifyMixpanelUser = (userId: string): void => {
  void (async () => {
    const mixpanelSdk = await loadMixpanelSdk();

    if (mixpanelSdk === null) {
      return;
    }

    if (identifiedUserId === userId) {
      return;
    }

    mixpanelSdk.identify(userId);
    mixpanelSdk.register({
      user_id: userId,
    });
    identifiedUserId = userId;
  })().catch((error: unknown) => {
    const errorMessage = error instanceof Error ? error.message : String(error);

    logWarn('analytics.mixpanel.identify_failed', 'Mixpanel identify failed', {
      trace_id: getTraceId(),
      user_id: userId,
      error: errorMessage,
    });
  });
};

export const identifyAndTrackMixpanelEvent = (
  userId: string,
  eventName: string,
  properties?: Record<string, unknown>
): void => {
  void (async () => {
    const mixpanelSdk = await loadMixpanelSdk();

    if (mixpanelSdk === null) {
      return;
    }

    if (identifiedUserId !== userId) {
      mixpanelSdk.identify(userId);
      mixpanelSdk.register({
        user_id: userId,
      });
      identifiedUserId = userId;
    }

    const baseEventProperties = buildBaseEventProperties();
    const eventProperties = {
      ...baseEventProperties,
      ...(properties ?? {}),
    };

    mixpanelSdk.track(eventName, eventProperties);
  })().catch((error: unknown) => {
    const errorMessage = error instanceof Error ? error.message : String(error);

    logWarn(
      'analytics.mixpanel.identify_track_failed',
      'Mixpanel identify-and-track failed',
      {
        trace_id: getTraceId(),
        mixpanel_event_name: eventName,
        user_id: userId,
        error: errorMessage,
      }
    );
  });
};

export const trackMixpanelEvent = (
  eventName: string,
  properties?: Record<string, unknown>
): void => {
  void (async () => {
    const mixpanelSdk = await loadMixpanelSdk();

    if (mixpanelSdk === null) {
      return;
    }

    const baseEventProperties = buildBaseEventProperties();
    const eventProperties = {
      ...baseEventProperties,
      ...(properties ?? {}),
    };

    mixpanelSdk.track(eventName, eventProperties);
  })().catch((error: unknown) => {
    const errorMessage = error instanceof Error ? error.message : String(error);

    logWarn('analytics.mixpanel.track_failed', 'Mixpanel browser track failed', {
      trace_id: getTraceId(),
      mixpanel_event_name: eventName,
      error: errorMessage,
    });
  });
};

export const enterMixpanelReplaySurface = (surface: MixpanelReplaySurface): void => {
  void (async () => {
    const config = currentMixpanelConfig();

    if (!config.replayEnabled) {
      return;
    }

    const mixpanelSdk = await loadMixpanelSdk();

    if (mixpanelSdk === null) {
      return;
    }

    if (activeReplaySurfaces.has(surface)) {
      return;
    }

    const shouldStartRecording = activeReplaySurfaces.size === 0;
    activeReplaySurfaces.add(surface);

    if (!shouldStartRecording) {
      return;
    }

    mixpanelSdk.start_session_recording();
  })().catch((error: unknown) => {
    const errorMessage = error instanceof Error ? error.message : String(error);

    logWarn('analytics.mixpanel.replay_start_failed', 'Mixpanel replay start failed', {
      trace_id: getTraceId(),
      replay_surface: surface,
      error: errorMessage,
    });
  });
};

export const leaveMixpanelReplaySurface = (surface: MixpanelReplaySurface): void => {
  if (!activeReplaySurfaces.has(surface)) {
    return;
  }

  activeReplaySurfaces.delete(surface);

  if (activeReplaySurfaces.size > 0) {
    return;
  }

  if (loadedMixpanelSdk === null) {
    return;
  }

  try {
    loadedMixpanelSdk.stop_session_recording();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);

    logWarn('analytics.mixpanel.replay_stop_failed', 'Mixpanel replay stop failed', {
      trace_id: getTraceId(),
      replay_surface: surface,
      error: errorMessage,
    });
  }
};
